import { useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Clock, User, DollarSign, Video, MapPin, Save } from "lucide-react";

// Status → BTB palette
const STATUS_COLORS = {
  scheduled: { bg: "#FF331F", border: "#FF331F", text: "#FFFFFF" },
  completed: { bg: "#10b981", border: "#10b981", text: "#FFFFFF" },
  cancelled: { bg: "#3f3f46", border: "#52525b", text: "#a1a1aa" },
  no_show:   { bg: "#f59e0b", border: "#f59e0b", text: "#000000" },
};
const PAID_BORDER = "#10b981"; // green border outline when paid

export default function AdminCalendar() {
  const calendarRef = useRef(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null); // booking object opened in modal
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState({ status: "scheduled", starts_at: "", zoom_link: "", session_notes: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const events = useMemo(() => {
    const list = statusFilter === "all"
      ? bookings
      : bookings.filter((b) => (b.status || "scheduled") === statusFilter);
    return list.map((b) => {
      const status = b.status || "scheduled";
      const palette = STATUS_COLORS[status] || STATUS_COLORS.scheduled;
      const minutes = Number(b.duration) || 40;
      const start = b.starts_at;
      const end = start ? new Date(new Date(start).getTime() + minutes * 60000).toISOString() : null;
      const isPaid = b.payment_status === "paid";
      return {
        id: b.id,
        title: `${b.client_name} · ${b.package_name?.replace("Better Through Boxing — ", "").replace("Live Virtual Zoom Session ", "Session ") || "Session"}`,
        start,
        end,
        backgroundColor: palette.bg,
        borderColor: isPaid ? PAID_BORDER : palette.border,
        textColor: palette.text,
        extendedProps: { booking: b, isPaid, status },
      };
    });
  }, [bookings, statusFilter]);

  const onEventClick = (info) => {
    const b = info.event.extendedProps.booking;
    setSelected(b);
    const pad = (n) => String(n).padStart(2, "0");
    const d = b.starts_at ? new Date(b.starts_at) : null;
    const localDT = d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` : "";
    setEditing({
      status: b.status || "scheduled",
      starts_at: localDT,
      zoom_link: b.zoom_link || "",
      session_notes: b.session_notes || "",
    });
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const payload = {
        status: editing.status,
        zoom_link: editing.zoom_link || null,
        session_notes: editing.session_notes || null,
      };
      if (editing.starts_at) {
        payload.starts_at = new Date(editing.starts_at).toISOString();
      }
      await api.patch(`/bookings/${selected.id}`, payload);
      toast.success("Booking updated");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const upcoming = useMemo(() => {
    const now = new Date().toISOString();
    return bookings.filter((b) => (b.starts_at || "") >= now && (b.status || "scheduled") === "scheduled").length;
  }, [bookings]);

  return (
    <div className="p-8 md:p-12" data-testid="admin-calendar-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2 flex items-center gap-2">
            <CalendarIcon className="h-3.5 w-3.5" /> Schedule
          </div>
          <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Calendar.</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">
            {loading ? "Loading sessions…" : `${bookings.length} total · ${upcoming} upcoming`}. Click any event to view or edit details.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 bg-[#050505] border-[#27272A] rounded-sm text-xs uppercase tracking-widest" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="no_show">No-show</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-6 text-[10px] uppercase tracking-widest text-zinc-500" data-testid="calendar-legend">
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#FF331F]" /> Scheduled</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-emerald-500" /> Completed</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-zinc-700" /> Cancelled</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-amber-500" /> No-show</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 border-2 border-emerald-500" /> Paid (outline)</div>
      </div>

      <div className="btb-calendar border border-[#27272A] bg-[#0A0A0A] p-3 md:p-5" data-testid="calendar-container">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
          }}
          buttonText={{ today: "Today", month: "Month", week: "Week", day: "Day", list: "List" }}
          events={events}
          eventClick={onEventClick}
          height="auto"
          nowIndicator
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          firstDay={0}
          dayMaxEvents={3}
          eventTimeFormat={{ hour: "numeric", minute: "2-digit", meridiem: "short" }}
        />
      </div>

      {/* Detail / edit modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-lg" data-testid="booking-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">Session Details</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">
              View and update this session.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-5">
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2"><User className="h-4 w-4 text-[#FF331F] mt-0.5" /> <div><div className="text-white">{selected.client_name}</div><div className="text-xs text-zinc-500">{selected.client_email}</div></div></div>
                <div className="flex items-start gap-2"><Clock className="h-4 w-4 text-[#FF331F] mt-0.5" /> <div><div className="text-white">{new Date(selected.starts_at).toLocaleString()}</div><div className="text-xs text-zinc-500">{selected.duration} min · {selected.package_name}</div></div></div>
                <div className="flex items-start gap-2"><DollarSign className="h-4 w-4 text-[#FF331F] mt-0.5" /> <div><div className="text-white">${Number(selected.amount).toFixed(2)}</div><div className={`text-xs uppercase tracking-widest font-bold ${selected.payment_status === "paid" ? "text-emerald-400" : "text-zinc-500"}`}>{selected.payment_status === "paid" ? "Paid" : "Unpaid"}</div></div></div>
              </div>

              <div className="space-y-3 pt-3 border-t border-[#27272A]">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Clock className="h-3 w-3" /> Reschedule (date & time)</Label>
                  <Input
                    type="datetime-local"
                    value={editing.starts_at}
                    onChange={(e) => setEditing({ ...editing, starts_at: e.target.value })}
                    className="bg-[#050505] border-[#27272A] rounded-sm"
                    data-testid="cal-edit-starts-at"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="edit-status-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="no_show">No-show</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Video className="h-3 w-3" /> Zoom link</Label>
                  <Input
                    type="url"
                    placeholder="https://zoom.us/j/…"
                    value={editing.zoom_link}
                    onChange={(e) => setEditing({ ...editing, zoom_link: e.target.value })}
                    className="bg-[#050505] border-[#27272A] rounded-sm font-mono text-xs"
                    data-testid="edit-zoom-input"
                  />
                  {editing.zoom_link ? (
                    <a href={editing.zoom_link} target="_blank" rel="noreferrer" className="text-[11px] text-[#FF331F] hover:underline inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Open link</a>
                  ) : null}
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Session notes (private)</Label>
                  <Textarea
                    rows={3}
                    value={editing.session_notes}
                    onChange={(e) => setEditing({ ...editing, session_notes: e.target.value })}
                    className="bg-[#050505] border-[#27272A] rounded-sm"
                    data-testid="edit-notes-input"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#27272A]">
                <Button type="button" variant="outline" onClick={() => setSelected(null)} className="border-[#27272A] bg-[#050505] text-white hover:bg-[#121212] rounded-sm">Close</Button>
                <Button type="button" onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] font-bold uppercase tracking-widest rounded-sm" data-testid="save-booking-btn">
                  <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
